import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Info,
  ArrowRight,
  Calendar,
  Shield,
  AlertTriangle,
  XCircle,
} from "lucide-react";

export default function HailDamageRoofRepairSaskatchewan() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/hail-damage-roof-repair-saskatchewan#article",
          headline:
            "Hail Damage Roof Repair in Saskatchewan: What Every Homeowner Should Know",
          description:
            "Discover the signs of hail damage, how to navigate insurance claims, and what to do after a severe Saskatchewan hailstorm to protect your home's roof.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-03-15",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/hail-damage-roof-repair-saskatchewan",
          url: "https://www.saskroofingcompany.com/blog/hail-damage-roof-repair-saskatchewan",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Hail Damage Roof Repair in Saskatchewan",
              item: "https://www.saskroofingcompany.com/blog/hail-damage-roof-repair-saskatchewan",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Does insurance cover hail damage to my roof in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, standard homeowner's insurance policies in Saskatchewan typically cover roof repair or replacement resulting from severe hailstorms, minus your deductible. It's important to document the damage and act promptly.",
          },
        },
        {
          "@type": "Question",
          name: "How long do I have to file a hail damage claim?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most insurance companies require you to file a claim within one to two years of the date of the hailstorm. However, filing as soon as possible prevents further damage, which might not be covered if you delay.",
          },
        },
        {
          "@type": "Question",
          name: "Should I call my insurance company or a roofer first?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We recommend calling a trusted local roofer for a free inspection first. A roofer can assess if the damage is severe enough to warrant a claim, potentially saving you from filing a zero-pay claim that could affect your premiums.",
          },
        },
        {
          "@type": "Question",
          name: "How do I document roof damage after a hailstorm?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Document damage safely from the ground by taking photos of dented gutters, downspouts, siding, and any shingle granules accumulating at the bottom of downspouts. Avoid climbing onto the roof yourself; leave that to a professional.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Hail Damage Roof Repair Saskatchewan | Sask Roofing"
        description="Discover the signs of hail damage, how to navigate insurance claims, and what to do after a severe Saskatchewan hailstorm to protect your home's roof."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/c465427c-c55b-4937-bd09-e8ebb5af446a.png&output=webp&q=60"
            alt="Hail damaged asphalt shingles on a roof after a severe storm in Saskatchewan"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published March 15, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Hail Damage Roof Repair in Saskatchewan: What Every Homeowner
              Should Know
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Saskatchewan is known for its legendary prairie hailstorms. When
              ice falls from the sky, your roof takes the brunt of the impact.
              Here is exactly what you need to do to protect your home and
              navigate the insurance process.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                Living in Saskatchewan means accepting that severe weather is
                just part of the deal. Between May and August, the prairie skies
                can turn dark in minutes, unleashing hailstones ranging from
                pea-sized to golf balls or larger. Because your roof is the
                first line of defense, it absorbs the majority of this impact.
              </p>
              <p>
                The problem with hail damage is that it's rarely obvious from
                the ground. Unlike a missing shingle torn off by high winds,
                hail damage often hides in plain sight, slowly degrading your
                roof's integrity until a major leak occurs. In this guide, we'll
                walk you through exactly what hail does to a roof, how to spot
                the warning signs, and the right way to handle the insurance
                claims process to ensure you aren't left footing the bill for{" "}
                <Link
                  to="/services/storm-damage"
                  className="text-primary font-medium hover:underline"
                >
                  storm damage repair
                </Link>
                .
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <Info className="w-6 h-6 text-primary" />
                The Short Answer (TL;DR)
              </h2>
              <p className="text-lg text-muted-foreground">
                Hail damage compromises your shingles by knocking off the
                protective granules, exposing the asphalt to UV degradation. If
                you suspect damage after a storm,{" "}
                <strong>do not climb on the roof</strong>. Document visible
                damage from the ground (like dented gutters) and book a{" "}
                <Link
                  to="/services/inspections"
                  className="text-primary font-medium hover:underline"
                >
                  free roof inspection
                </Link>{" "}
                before calling your insurance. A professional roofer will tell
                you if the damage warrants a claim.
              </p>
            </div>

            {/* Hail Season Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                Hail Season in Saskatchewan: When and How Hard
              </h2>
              <p className="text-lg text-muted-foreground">
                Saskatchewan sits squarely in "Hail Alley," a geographic region
                notorious for atmospheric conditions that produce massive
                hailstorms. According to Environment Canada data, the province
                experiences some of the highest frequencies of severe hail
                events in the country.
              </p>
              <p className="text-lg text-muted-foreground">
                The peak season runs from late May through August. During these
                months, the intense daytime heating of the prairies collides
                with cooler upper-atmosphere air, creating massive updrafts.
                These updrafts hold moisture high in the freezing levels of the
                cloud, allowing hailstones to grow significantly before they
                finally become too heavy and plummet to the ground. If you live
                in the corridor from Swift Current through Moose Jaw, Regina,
                and up to Yorkton, you are in the bullseye for these destructive
                events.
              </p>

              <div className="my-8 rounded-2xl overflow-hidden shadow-md">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f7760c20-4b61-43fa-98c8-e8bf33045b85.png&output=webp&q=60"
                  alt="Dented aluminum gutters showing clear signs of severe hail damage"
                  className="w-full h-auto object-cover"
                />
                <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                  Dented gutters and downspouts are often the first visible
                  indicators of a severe hailstorm.
                </p>
              </div>
            </div>

            {/* What Hail Does Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                What Hail Does to a Roof
              </h2>
              <p className="text-lg text-muted-foreground">
                When a hailstone strikes an asphalt shingle, it acts like a
                miniature hammer. The severity of the damage depends on the size
                of the hail, the wind speed, the angle of the roof, and the age
                of the shingles. Here is what is happening up there:
              </p>
              <ul className="space-y-4 text-lg text-muted-foreground">
                <li className="flex gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0 mt-1" />
                  <div>
                    <strong>Granule Loss:</strong> Shingles are coated with
                    ceramic granules that protect the asphalt layer from the
                    sun's UV rays. Hail knocks these granules loose. Once the
                    asphalt is exposed to the sun, it quickly dries out, cracks,
                    and fails.
                  </div>
                </li>
                <li className="flex gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0 mt-1" />
                  <div>
                    <strong>Bruising and Mat Fracture:</strong> Even if the
                    granules stay attached initially, the impact can fracture
                    the fiberglass mat underneath the asphalt. This creates a
                    "bruise" that compromises the shingle's ability to shed
                    water.
                  </div>
                </li>
                <li className="flex gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0 mt-1" />
                  <div>
                    <strong>Flashing Damage:</strong> Hail easily dents the thin
                    metal used for roof vents, chimney flashings, and exhaust
                    caps, potentially breaking the waterproof seal around these
                    critical penetrations.
                  </div>
                </li>
              </ul>
            </div>

            {/* Callout Card */}
            <div className="bg-accent/10 border border-accent/20 p-6 rounded-xl flex gap-4 my-8">
              <Shield className="w-8 h-8 text-accent shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  The Danger of Waiting
                </h3>
                <p className="text-muted-foreground">
                  A hail-damaged roof rarely leaks the day after the storm. It
                  might take six months or a year for the exposed asphalt to
                  crack and allow water into your attic. By the time you notice
                  a water stain on your ceiling, the structural damage to your
                  roof decking and insulation can be severe.
                </p>
              </div>
            </div>

            {/* 5 Signs Listicle */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                5 Signs Your Roof Sustained Hail Damage
              </h2>
              <p className="text-lg text-muted-foreground">
                You don't need a ladder to spot the early warning signs. Walk
                around your property after the storm passes and look for these
                indicators:
              </p>

              <div className="space-y-6 mt-6">
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Dented Gutters and Downspouts
                    </h3>
                    <p className="text-muted-foreground">
                      Aluminum gutters are soft. If you see dimples or dents
                      along the face of your eavestroughs, or if the downspouts
                      are dented, the roof almost certainly took a beating as
                      well.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Granules in the Gutters
                    </h3>
                    <p className="text-muted-foreground">
                      Look at the bottom of your downspouts where water exits.
                      If you see an unusually large accumulation of black, sandy
                      shingle granules, the storm scoured the protective layer
                      off your roof.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Dented Siding or Window Casings
                    </h3>
                    <p className="text-muted-foreground">
                      Wind-driven hail often strikes the sides of the house.
                      Check your vinyl or aluminum siding for cracks, holes, or
                      dents. Check the soft metal wrapping around your windows.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Damage to Soft Metals on the Roof
                    </h3>
                    <p className="text-muted-foreground">
                      If you have binoculars, look up at the roof vents, chimney
                      caps, and turbine vents. Dents on these soft metals are
                      the easiest way for insurance adjusters to verify a hail
                      event.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Dark Spots on Shingles
                    </h3>
                    <p className="text-muted-foreground">
                      Again, using binoculars, look for dark, circular spots on
                      the shingles. These are "bruises" where the granules have
                      been knocked away, exposing the dark asphalt beneath.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* What to Do Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                What to Do After a Major Hailstorm
              </h2>
              <p className="text-lg text-muted-foreground">
                If a major storm just rolled through your neighborhood, follow
                these steps to protect your property and your wallet:
              </p>

              <div className="bg-muted/30 p-6 rounded-xl border my-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>Stay off the roof.</strong> Hail can loosen
                      granules making the roof incredibly slippery. Leave the
                      climbing to insured professionals.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>Document from the ground.</strong> Take clear
                      photos of the hailstones (put a coin next to them for
                      scale), dented gutters, damaged siding, and any broken
                      windows or skylights.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>Call for an inspection.</strong> Contact a
                      reputable, local Saskatchewan roofing company to perform a
                      thorough, on-roof inspection.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>Keep all receipts.</strong> If you need emergency
                      tarping to stop an active leak, keep the invoice. Your
                      insurance company will typically reimburse you for
                      emergency mitigation efforts.
                    </p>
                  </li>
                </ul>
              </div>

              <div className="my-8 rounded-2xl overflow-hidden shadow-md">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/037d8293-812e-4056-a293-143d5515d649.png&output=webp&q=60"
                  alt="Roofing professional inspecting shingles for hail damage after a storm"
                  className="w-full h-auto object-cover"
                />
                <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                  A professional inspection is the only way to accurately assess
                  the extent of hail bruising on the shingle mat.
                </p>
              </div>
            </div>

            {/* Insurance Claims Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                Insurance Claims: The Process
              </h2>
              <p className="text-lg text-muted-foreground">
                Navigating an insurance claim can be stressful, but
                understanding the process makes it much easier. Standard
                homeowner's policies in Saskatchewan cover hail damage, minus
                your deductible (which typically ranges from $1,000 to $5,000).
              </p>
              <p className="text-lg text-muted-foreground">
                <strong>
                  Why you should call a roofer before the adjuster:
                </strong>{" "}
                Insurance adjusters are busy, especially after a major regional
                storm. They may miss subtle damage. When you call Sask Roofing
                Company first, we document the damage with high-resolution
                photos and detailed reports. We can even be on-site when the
                adjuster arrives to ensure they see exactly what we see,
                advocating on your behalf to ensure you receive a fair
                settlement.
              </p>
            </div>

            {/* What if Damage isn't Severe */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                What if the Damage Isn't Severe?
              </h2>
              <p className="text-lg text-muted-foreground">
                Not every storm requires a new roof. If the hail was small or
                the winds were light, your roof might be perfectly fine.
              </p>
              <p className="text-lg text-muted-foreground">
                If you call your insurance company immediately and file a claim,
                but the adjuster determines the damage doesn't exceed your
                deductible, you now have a "zero-pay claim" on your record. This
                can negatively impact your future premiums or your ability to
                switch carriers. This is exactly why an honest, professional
                inspection is crucial. We will tell you straight if a claim
                isn't worth filing. Honest assessment builds trust; aggressive
                selling doesn't.
              </p>
            </div>

            {/* Repair vs Replace */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                The Repair vs. Replacement Decision
              </h2>
              <p className="text-lg text-muted-foreground">
                If the damage is isolated to a few shingles or a specific slope,
                a repair might be sufficient. However, if the bruising is
                widespread, a full replacement is necessary.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    When to Repair
                  </h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Damage is highly localized (e.g., one slope)</li>
                    <li>• Roof is relatively new (under 10 years old)</li>
                    <li>• Shingles are still pliable and not brittle</li>
                    <li>
                      • Cost of repair is significantly less than deductible
                    </li>
                  </ul>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    When to Replace
                  </h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Widespread granule loss across multiple slopes</li>
                    <li>• Numerous bruised or fractured shingles</li>
                    <li>• Roof is older (15+ years) and already degrading</li>
                    <li>• Insurance approves a full replacement claim</li>
                  </ul>
                </div>
              </div>
              <p className="text-lg text-muted-foreground">
                <em>
                  Note: If you are concerned about{" "}
                  <Link
                    to="/roofing-cost-guide-saskatchewan"
                    className="text-primary hover:underline"
                  >
                    roofing costs
                  </Link>
                  , remember that insurance policies often include a "matching
                  endorsement," meaning if the damaged shingles cannot be
                  perfectly matched with new ones, the insurance company must
                  replace the entire roof to ensure a uniform appearance.
                </em>
              </p>
            </div>

            <div className="my-8 rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/88e5be79-fa67-4c0d-836a-50c5f6ba4822.png&output=webp&q=60"
                alt="Completed residential roof restoration after severe storm damage"
                className="w-full h-auto object-cover"
              />
              <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                A full storm restoration using Class 4 impact-resistant shingles
                provides peace of mind for the next storm.
              </p>
            </div>

            {/* FAQ */}
            <div className="space-y-6 pt-8 border-t">
              <h2 className="text-3xl font-bold text-foreground">
                Hail Damage FAQ
              </h2>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Does insurance cover hail damage to my roof in Saskatchewan?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Yes, standard homeowner's insurance policies in Saskatchewan
                    typically cover roof repair or replacement resulting from
                    severe hailstorms, minus your deductible. It's important to
                    document the damage and act promptly.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How long do I have to file a hail damage claim?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Most insurance companies require you to file a claim within
                    one to two years of the date of the hailstorm. However,
                    filing as soon as possible prevents further damage, which
                    might not be covered if you delay.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Should I call my insurance company or a roofer first?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    We recommend calling a trusted local roofer for a free
                    inspection first. A roofer can assess if the damage is
                    severe enough to warrant a claim, potentially saving you
                    from filing a zero-pay claim that could affect your
                    premiums.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How do I document roof damage after a hailstorm?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Document damage safely from the ground by taking photos of
                    dented gutters, downspouts, siding, and any shingle granules
                    accumulating at the bottom of downspouts. Avoid climbing
                    onto the roof yourself; leave that to a professional.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-muted/50 text-center border-t">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">Worried About Storm Damage?</h2>
          <p className="text-xl text-muted-foreground">
            Don't wait for a leak to confirm your suspicions. Book a free,
            no-obligation inspection today. We'll document any damage with
            photos and help you decide if filing an insurance claim is the right
            move for your home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Book a Free Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-primary text-primary hover:bg-primary/5"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
